import axios from "axios";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
export const http = axios.create({ baseURL: API, withCredentials: true });

export function formatErr(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false)).finally(() => setReady(true));
  }, []);

  const login = async (email, password) => {
    const { data } = await http.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await http.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };
  const loginGoogle = async (credential) => {
    const { data } = await http.post("/auth/google", { credential });
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    await http.post("/auth/logout");
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, ready, login, register, loginGoogle, logout, setUser }}>{children}</AuthCtx.Provider>;
}

export function ToastCtxProvider({ children }) {
  // Flash message. Par défaut 5 s (actions importantes) ; durée/type surchargeables :
  //   toast("message")                      → 5 s, neutre
  //   toast("Erreur…", { type: "error" })   → 5 s, rouge
  //   toast("Copié", { duration: 2000 })    → 2 s
  const [toast, setToast] = useState({ msg: "", type: "info" });
  const timer = useRef();
  const show = (m, opts = {}) => {
    const { duration = 5000, type = "info" } = typeof opts === "number" ? { duration: opts } : opts;
    setToast({ msg: m, type });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast((s) => ({ ...s, msg: "" })), duration);
  };
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div className={`toast toast-${toast.type} ${toast.msg ? "show" : ""}`} data-testid="toast">{toast.msg}</div>
    </ToastCtx.Provider>
  );
}
const ToastCtx = createContext(() => {});
export const useToast = () => useContext(ToastCtx);

// ---- Favoris (localStorage) ----
// ---- Internationalisation FR / EN ----
const TR = {
  fr: {
    // Navigation commune
    solutions: "Solutions", favoris: "Favoris", abonnements: "Abonnements",
    connexion: "Connexion", deconnexion: "Déconnexion", admin: "Admin AfriFuté",
    mon_espace: "Mon espace", loading: "Chargement…",
    nav_proposer: "Proposer une solution",
    // Accueil / catalogue
    hero_title_1: "Découvrez les", hero_title_2: "solutions africaines",
    hero_title_3: "utiles, expliquées simplement.",
    hero_sub: "Le moteur de connaissance AfriFuté : startups, applications, plateformes et services innovants, documentés de façon claire, concrète et actionnable.",
    search_ph: "Rechercher une solution, un secteur, une fonction…",
    all_solutions: "Toutes les solutions", featured: "Mises en avant", sort_by: "Trier par",
    cat_fonction: "Fonction", cat_pays: "Pays", cat_portee: "Portée géographique", cat_public: "Public cible",
    cat_reset: "Réinitialiser", cat_loading: "Chargement des solutions…",
    cat_sort_recent: "Plus récentes", cat_sort_nom: "Nom (A-Z)", cat_sort_note: "Mieux notées", cat_sort_vues: "Plus consultées",
    cat_empty: "Aucune solution ne correspond à votre recherche.", cat_fav_label: "Favori",
    badge_certifie: "CERTIFIÉ", badge_premium: "PREMIUM", badge_enrichi: "ENRICHI",
    // Favoris
    favoris_title_1: "Mes", favoris_title_2: "favoris",
    favoris_sub: "Retrouvez les solutions africaines que vous avez sauvegardées.",
    favoris_empty: "Aucun favori pour l'instant. Cliquez sur le cœur d'une solution pour l'ajouter ici.",
    // Connexion / inscription
    login_title: "Connexion", register_title: "Créer un compte",
    login_sub: "Accédez à l'administration AfriFuté ou à votre espace entreprise.",
    login_name_label: "Nom / Entreprise", login_email_label: "Email", login_password_label: "Mot de passe",
    login_submit: "Se connecter", register_submit: "Créer mon compte",
    login_no_account: "Pas de compte ?", login_already_account: "Déjà inscrit ?",
    login_admin_note: "Un compte est toujours créé côté entreprise. Les accès équipe AfriFuté sont attribués manuellement, pour des raisons de sécurité.",
    // Tarifs
    pr_title_1: "NOS", pr_title_2: "ABONNEMENTS",
    pr_sub: "Choisissez la formule qui correspond le mieux à vos besoins et développez votre visibilité sur AfriFuté Atlas.",
    pr_col_fonctionnalites: "FONCTIONNALITÉS", pr_populaire: "POPULAIRE",
    pr_creer: "Créer une fiche solution", pr_revendiquer: "Revendiquer une fiche existante",
    pr_max_fiches: "Nombre de fiches", pr_illimite: "Illimité", pr_fiche: "fiche",
    pr_maj: "Mises à jour des fiches", pr_val_soumise: "Soumise à validation", pr_val_prioritaire: "Validation prioritaire",
    pr_validation: "Validation des modifications", pr_stats: "Statistiques de base", pr_stats_avancees: "Statistiques avancées",
    pr_export: "Export (PDF, Excel, CSV)", pr_share: "Téléchargements & partages", pr_collaborateurs: "Nombre de collaborateurs",
    pr_badge: "Badges & mise en avant", pr_badge_ref: "Référencé", pr_badge_premium: "Premium", pr_badge_ent: "Premium + Certifié",
    pr_support: "Support", pr_api: "Accès API",
    pr_cta_free: "COMMENCER GRATUITEMENT", pr_cta_choisir: "CHOISIR",
    pr_toast_active: "Abonnement activé (paiement simulé) :", pr_toast_erreur: "Erreur : ",
    pr_reassure_1_t: "Sécurisé & Fiable", pr_reassure_1_s: "Vos données sont protégées",
    pr_reassure_2_t: "Sans engagement", pr_reassure_2_s: "Résiliez à tout moment",
    pr_reassure_3_t: "Paiement sécurisé", pr_reassure_3_s: "Mobile Money, Carte, Virement",
    pr_reassure_4_t: "Besoin d'aide ?", pr_reassure_4_s: "Contactez-nous à tout moment",
    // Espace entreprise
    ent_tab_overview: "Vue d'ensemble", ent_tab_solutions: "Mes fiches", ent_tab_validations: "Validations",
    ent_tab_claims: "Revendications", ent_tab_notifications: "Notifications", ent_tab_plan: "Mon abonnement",
    ent_breadcrumb_overview: "ATLAS / ESPACE ENTREPRISE", ent_breadcrumb_solutions: "ATLAS / MES FICHES",
    ent_breadcrumb_validations: "ATLAS / VALIDATIONS", ent_breadcrumb_claims: "ATLAS / REVENDICATIONS",
    ent_breadcrumb_notifications: "ATLAS / NOTIFICATIONS", ent_breadcrumb_plan: "ATLAS / ABONNEMENT",
    ent_bonjour: "Bonjour", ent_plan: "Plan", ent_nouvelle_fiche: "Nouvelle fiche",
    ent_stat_fiches: "Mes fiches", ent_stat_vues: "Vues totales", ent_stat_telechargements: "Téléchargements",
    ent_stat_validations_attente: "Validations en attente",
    ent_workflow_note_1: "L'entreprise enrichit,", ent_workflow_note_2: "AfriFuté valide",
    ent_workflow_note_3: ". Toute création, modification ou revendication passe par validation.",
    ent_ameliorer_plan: "Améliorer mon plan",
    ent_titre_solutions: "Mes fiches solutions", ent_solutions_vide: "Aucune fiche. Créez votre première solution - elle sera soumise à validation AfriFuté.",
    ent_col_nom: "Nom", ent_col_score: "Score Atlas", ent_col_editorial: "Éditorial", ent_col_visibilite: "Visibilité", ent_col_actions: "Actions",
    ent_modifier: "Modifier", ent_voir: "Voir",
    ent_titre_validations: "État des validations", ent_validations_vide: "Aucune demande en cours.",
    ent_col_fiche: "Fiche", ent_col_type: "Type", ent_col_statut: "Statut", ent_col_note: "Note AfriFuté",
    ent_titre_claims: "Mes revendications", ent_claims_vide: "Aucune revendication. Depuis une fiche, cliquez « Revendiquer ».",
    ent_col_solution: "Solution",
    ent_titre_notifications: "Notifications", ent_tout_marquer_lu: "Tout marquer comme lu", ent_notifs_vide: "Aucune notification.",
    ent_titre_plan: "Mon abonnement", ent_limite_fiches: "Fiches autorisées", ent_limite_export: "Export & partage",
    ent_limite_stats: "Statistiques avancées", ent_oui: "Oui", ent_non: "Non", ent_voir_abonnements: "Voir les abonnements",
    // Admin
    adm_tab_dashboard: "Vue globale", adm_tab_solutions: "Solutions", adm_tab_validations: "Validations",
    adm_tab_soumissions: "Soumissions", adm_tab_claims: "Revendications", adm_tab_reviews: "Avis",
    adm_tab_reports: "Signalements", adm_tab_users: "Utilisateurs", adm_tab_pricing: "Abonnements", adm_tab_modules: "Modules",
    adm_bc_dashboard: "ADMIN / DASHBOARD", adm_bc_solutions: "ADMIN / SOLUTIONS", adm_bc_soumissions: "ADMIN / SOUMISSIONS",
    adm_bc_reviews: "ADMIN / AVIS", adm_bc_reports: "ADMIN / SIGNALEMENTS", adm_bc_users: "ADMIN / UTILISATEURS",
    adm_bc_pricing: "ADMIN / ABONNEMENTS", adm_bc_modules: "ADMIN / MODULES",
    adm_nouvelle_solution: "Nouvelle solution",
    adm_stat_solutions: "Solutions", adm_stat_actives: "Actives", adm_stat_vues: "Vues cumulées",
    adm_stat_utilisateurs: "Utilisateurs", adm_stat_validations: "Validations en attente", adm_stat_avis: "Avis à modérer",
    adm_titre_solutions: "Gestion des solutions", adm_col_score: "Score", adm_col_live: "Live",
    adm_active: "Activé", adm_inactive: "Inactif",
    adm_titre_validations: "Workflow de validation", adm_aucune_demande: "Aucune demande.",
    adm_col_par: "Par", adm_approuver: "Approuver", adm_correction: "Correction", adm_rejeter: "Rejeter",
    adm_titre_soumissions: "Soumissions (formulaire simple)", adm_aucune_soumission: "Aucune soumission.",
    adm_col_action: "Action", adm_valider: "Valider",
    adm_col_demandeur: "Demandeur",
    adm_titre_reviews: "Modération des avis", adm_aucun_avis: "Aucun avis.",
    adm_col_auteur: "Auteur", adm_col_note: "Note", adm_publier: "Publier",
    adm_aucun_signalement: "Aucun signalement.", adm_col_message: "Message", adm_traiter: "Traiter",
    adm_titre_users: "Utilisateurs & abonnements", adm_col_email: "Email", adm_col_role: "Rôle",
    adm_titre_pricing: "Tarifs & abonnements", adm_enregistrer_tarifs: "Enregistrer les tarifs",
    adm_pricing_note: "Modifiez les prix (abonnement mensuel & achat unique) et les limites de chaque formule.",
    adm_prix_mois: "Prix / mois", adm_prix_unique: "Prix achat unique", adm_nb_fiches: "Nombre de fiches (-1 = illimité)",
    adm_tagline: "Tagline",
    adm_titre_modules: "Modules Atlas",
    adm_mod_editorial: "Analyses AfriFuté", adm_mod_submission: "Soumission entreprises", adm_mod_claims: "Revendication de profil",
    adm_mod_reviews: "Avis modérés", adm_mod_reports: "Signalements", adm_mod_seo: "Schema.org & sitemap",
    adm_mod_media: "Articles afrifute.com", adm_mod_subs: "Abonnements & limites", adm_mod_analytics: "Statistiques",
    adm_mod_export: "Export Premium", adm_desactive: "Désactivé",
    adm_toast_publie: "Publié", adm_toast_decision: "Décision enregistrée", adm_toast_plan_maj: "Plan mis à jour",
    adm_toast_tarifs: "Tarifs enregistrés", adm_prompt_correction: "Note / correction demandée :",
    adm_tab_reverif: "A re-verifier", adm_stat_reverif: "Fiches à re-verifier",
    adm_bc_reverif: "ADMIN / VERIFICATION PERIODIQUE", adm_titre_reverif: "Fiches à re-verifier",
    adm_reverif_note_1: "Une fiche active est signalee ici si elle n'a jamais ete verifiee, ou si sa derniere verification remonte a plus de",
    adm_reverif_note_2: "mois.",
    adm_reverif_vide: "Aucune fiche a re-verifier pour le moment.",
    adm_col_derniere_verif: "Derniere verification", adm_jamais_verifie: "Jamais verifie",
    adm_marquer_reverifie: "Marquer comme verifie aujourd'hui", adm_toast_reverifie: "Fiche marquee comme verifiee",
    adm_tab_reglages: "Réglages avancés", adm_bc_reglages: "ADMIN / RÉGLAGES AVANCÉS", adm_titre_reglages: "Réglages avancés",
    adm_reglages_taxonomies: "Taxonomies", adm_reglages_taxo_note: "Secteurs, fonctions, pays, portées, publics, types, maturités, plateformes",
    adm_retirer: "Retirer", adm_ajouter_terme: "Nouveau terme…", adm_ajouter: "Ajouter", adm_enregistrer_taxo: "Enregistrer les taxonomies",
    adm_reglages_seo: "SEO", adm_seo_titre: "Titre du site", adm_seo_description: "Description du site", adm_enregistrer_seo: "Enregistrer le SEO",
    adm_reglages_email: "Modèles d'email", adm_email_note: "S'applique à tous les emails automatiques envoyés (validations, refus, revendications…).",
    adm_email_prefixe: "Préfixe du sujet", adm_email_signature: "Signature en bas d'email", adm_enregistrer_email: "Enregistrer",
    adm_reglages_api: "Clé API", adm_api_note: "Cette clé donne un accès public en lecture seule aux fiches publiées (nom, description, secteur, site officiel), utile pour une intégration externe comme afrifute.com. Elle doit être fournie dans l'en-tête X-API-Key de chaque requête.",
    adm_api_cle: "Clé actuelle", adm_regenerer_cle: "Régénérer la clé",
    adm_confirm_regen_key: "Régénérer la clé API ? L'ancienne cessera de fonctionner immédiatement.",
    adm_reglages_journal: "Journal des actions", adm_journal_vide: "Aucune action enregistrée pour le moment.",
    adm_col_date: "Date", adm_col_action_log: "Action", adm_col_details: "Détails",
    adm_toast_taxo: "Taxonomies enregistrées", adm_toast_seo: "Réglages SEO enregistrés",
    adm_toast_email: "Modèles d'email enregistrés", adm_toast_cle_regen: "Nouvelle clé générée",
    adm_taxo_alerte_intro: "Les termes suivants sont encore utilises par des fiches existantes :",
    adm_taxo_alerte_confirmer: "Les retirer quand meme ? Les fiches concernees garderont leur valeur actuelle, mais ce terme n'apparaitra plus dans les filtres.",

    // Fiche solution
    sp_retour: "Retour", sp_vues: "vues", sp_score_atlas: "Score Atlas",
    sp_score_title: "Knowledge Score Atlas : richesse, média, vérification, contacts",
    sp_revendiquee: "Fiche revendiquée", sp_badge_certifiee: "Solution certifiée", sp_badge_enrichi_profil: "Profil enrichi",
    sp_verifie_le: "Vérifié le",
    sp_site_officiel: "Site officiel", sp_favori: "Favori", sp_ajouter_favoris: "Ajouter aux favoris",
    sp_revendiquer: "Revendiquer", sp_revendiquer_titre: "Revendiquer",
    sp_claim_message: "Message à l'équipe AfriFuté", sp_claim_preuve: "Preuve (email pro, site, lien officiel…)",
    sp_envoyer_demande: "Envoyer la demande",
    sp_presentation: "Présentation", sp_probleme_resolu: "Problème résolu", sp_public_cible: "Public cible",
    sp_classification: "Classification", sp_captures: "Captures d'écran", sp_capture: "capture",
    sp_metriques: "Métriques clés", sp_informations: "Informations", sp_creee_en: "Créée en", sp_langues: "Langues",
    sp_cas_usage: "Cas d'usage", sp_fonctionnalites: "Fonctionnalités principales", sp_services: "Services proposés",
    sp_avlim_titre: "Avantages et limites", sp_avantages: "Avantages", sp_limites: "Limites",
    sp_tarifs: "Tarifs", sp_analyse: "Notre analyse AfriFuté", sp_faq: "Questions fréquentes",
    sp_similaires: "Solutions similaires", sp_articles_lies: "Articles AfriFuté liés",
    sp_badges_atlas: "Badges Atlas", sp_video: "Vidéo", sp_voir_video: "Voir la vidéo de présentation",
    sp_galerie: "Galerie", sp_fondateurs: "Fondateurs", sp_equipe: "Équipe",
    sp_ecosysteme: "Écosystème & reconnaissance", sp_integrations: "Intégrations", sp_partenaires: "Partenaires",
    sp_certifications: "Certifications", sp_distinctions: "Distinctions", sp_ressources: "Centre de ressources",
    sp_avis: "Avis", sp_aucun_avis: "Aucun avis publié pour l'instant.", sp_utilisateur: "Utilisateur",
    sp_votre_note: "Votre note", sp_partagez_experience: "Partagez votre expérience…", sp_soumettre_avis: "Soumettre mon avis",
    sp_export_signalement: "Export & signalement", sp_exporter_json: "Exporter JSON", sp_exporter_csv: "Exporter CSV",
    sp_imprimer: "Imprimer", sp_signaler: "Signaler un problème",
    sp_export_reserve: "L'export (PDF/CSV/JSON) et l'impression sont réservés aux abonnements Premium et Enterprise.",
    sp_voir_offres: "Voir les offres", sp_type_probleme: "Type de problème", sp_decrivez_probleme: "Décrivez le problème…",
    sp_envoyer_signalement: "Envoyer le signalement", sp_partager: "Partager cette fiche", sp_copier_lien: "Copier le lien",
    sp_reseaux: "Réseaux", sp_not_found: "Solution introuvable.",
    sp_toast_login_review: "Connectez-vous pour laisser un avis", sp_toast_review_sent: "Avis soumis - en attente de validation",
    sp_toast_login_report: "Connectez-vous pour signaler", sp_toast_report_sent: "Signalement envoyé à AfriFuté",
    sp_toast_export_locked: "Export réservé aux abonnements Premium", sp_toast_claim_sent: "Demande de revendication envoyée - validation AfriFuté en cours",
    sp_toast_erreur: "Erreur: ", sp_toast_erreur_deux: "Erreur : ", sp_toast_no_site: "Site web non renseigné",
    sp_toast_login_entreprise: "Connectez-vous en entreprise pour revendiquer", sp_toast_login_claim: "Connectez-vous pour revendiquer cette fiche",
    sp_toast_print_locked: "Impression réservée au Premium", sp_toast_lien_copie: "Lien copié !",
    sp_report_wrong: "informations erronées", sp_report_fraud: "fraude", sp_report_broken: "lien cassé",
    sp_report_closed: "entreprise fermée", sp_report_impersonation: "usurpation", sp_report_other: "autre",
  },
  en: {
    solutions: "Solutions", favoris: "Favorites", abonnements: "Pricing",
    connexion: "Sign in", deconnexion: "Sign out", admin: "AfriFuté Admin",
    mon_espace: "My space", loading: "Loading…",
    nav_proposer: "Submit a solution",
    hero_title_1: "Discover the", hero_title_2: "African solutions",
    hero_title_3: "that matter, explained simply.",
    hero_sub: "The AfriFuté knowledge engine: startups, apps, platforms and innovative services, documented in a clear, concrete and actionable way.",
    search_ph: "Search a solution, a sector, a function…",
    all_solutions: "All solutions", featured: "Featured", sort_by: "Sort by",
    cat_fonction: "Function", cat_pays: "Country", cat_portee: "Geographic reach", cat_public: "Target audience",
    cat_reset: "Reset", cat_loading: "Loading solutions…",
    cat_sort_recent: "Most recent", cat_sort_nom: "Name (A-Z)", cat_sort_note: "Top rated", cat_sort_vues: "Most viewed",
    cat_empty: "No solution matches your search.", cat_fav_label: "Favorite",
    badge_certifie: "CERTIFIED", badge_premium: "PREMIUM", badge_enrichi: "ENRICHED",
    favoris_title_1: "My", favoris_title_2: "favorites",
    favoris_sub: "Find the African solutions you've saved.",
    favoris_empty: "No favorites yet. Click the heart on a solution to add it here.",
    login_title: "Sign in", register_title: "Create an account",
    login_sub: "Access the AfriFuté admin area or your company space.",
    login_name_label: "Name / Company", login_email_label: "Email", login_password_label: "Password",
    login_submit: "Sign in", register_submit: "Create my account",
    login_no_account: "No account?", login_already_account: "Already registered?",
    login_admin_note: "An account is always created on the company side. AfriFuté team access is granted manually, for security reasons.",
    pr_title_1: "OUR", pr_title_2: "PLANS",
    pr_sub: "Choose the plan that best fits your needs and grow your visibility on AfriFuté Atlas.",
    pr_col_fonctionnalites: "FEATURES", pr_populaire: "POPULAR",
    pr_creer: "Create a solution profile", pr_revendiquer: "Claim an existing profile",
    pr_max_fiches: "Number of profiles", pr_illimite: "Unlimited", pr_fiche: "profile",
    pr_maj: "Profile updates", pr_val_soumise: "Subject to validation", pr_val_prioritaire: "Priority validation",
    pr_validation: "Change validation", pr_stats: "Basic statistics", pr_stats_avancees: "Advanced statistics",
    pr_export: "Export (PDF, Excel, CSV)", pr_share: "Downloads & sharing", pr_collaborateurs: "Number of collaborators",
    pr_badge: "Badges & spotlight", pr_badge_ref: "Listed", pr_badge_premium: "Premium", pr_badge_ent: "Premium + Certified",
    pr_support: "Support", pr_api: "API access",
    pr_cta_free: "START FOR FREE", pr_cta_choisir: "CHOOSE",
    pr_toast_active: "Subscription activated (simulated payment):", pr_toast_erreur: "Error: ",
    pr_reassure_1_t: "Secure & Reliable", pr_reassure_1_s: "Your data is protected",
    pr_reassure_2_t: "No commitment", pr_reassure_2_s: "Cancel anytime",
    pr_reassure_3_t: "Secure payment", pr_reassure_3_s: "Mobile Money, Card, Transfer",
    pr_reassure_4_t: "Need help?", pr_reassure_4_s: "Contact us anytime",
    // Company space
    ent_tab_overview: "Overview", ent_tab_solutions: "My profiles", ent_tab_validations: "Validations",
    ent_tab_claims: "Claims", ent_tab_notifications: "Notifications", ent_tab_plan: "My subscription",
    ent_breadcrumb_overview: "ATLAS / COMPANY SPACE", ent_breadcrumb_solutions: "ATLAS / MY PROFILES",
    ent_breadcrumb_validations: "ATLAS / VALIDATIONS", ent_breadcrumb_claims: "ATLAS / CLAIMS",
    ent_breadcrumb_notifications: "ATLAS / NOTIFICATIONS", ent_breadcrumb_plan: "ATLAS / SUBSCRIPTION",
    ent_bonjour: "Hello", ent_plan: "Plan", ent_nouvelle_fiche: "New profile",
    ent_stat_fiches: "My profiles", ent_stat_vues: "Total views", ent_stat_telechargements: "Downloads",
    ent_stat_validations_attente: "Pending validations",
    ent_workflow_note_1: "The company enriches,", ent_workflow_note_2: "AfriFuté validates",
    ent_workflow_note_3: ". Every creation, edit or claim goes through validation.",
    ent_ameliorer_plan: "Upgrade my plan",
    ent_titre_solutions: "My solution profiles", ent_solutions_vide: "No profile yet. Create your first solution - it will be submitted for AfriFuté validation.",
    ent_col_nom: "Name", ent_col_score: "Atlas score", ent_col_editorial: "Editorial", ent_col_visibilite: "Visibility", ent_col_actions: "Actions",
    ent_modifier: "Edit", ent_voir: "View",
    ent_titre_validations: "Validation status", ent_validations_vide: "No pending request.",
    ent_col_fiche: "Profile", ent_col_type: "Type", ent_col_statut: "Status", ent_col_note: "AfriFuté note",
    ent_titre_claims: "My claims", ent_claims_vide: "No claim yet. From a profile page, click \"Claim\".",
    ent_col_solution: "Solution",
    ent_titre_notifications: "Notifications", ent_tout_marquer_lu: "Mark all as read", ent_notifs_vide: "No notification.",
    ent_titre_plan: "My subscription", ent_limite_fiches: "Allowed profiles", ent_limite_export: "Export & sharing",
    ent_limite_stats: "Advanced statistics", ent_oui: "Yes", ent_non: "No", ent_voir_abonnements: "View plans",
    // Admin
    adm_tab_dashboard: "Overview", adm_tab_solutions: "Solutions", adm_tab_validations: "Validations",
    adm_tab_soumissions: "Submissions", adm_tab_claims: "Claims", adm_tab_reviews: "Reviews",
    adm_tab_reports: "Reports", adm_tab_users: "Users", adm_tab_pricing: "Plans", adm_tab_modules: "Modules",
    adm_bc_dashboard: "ADMIN / DASHBOARD", adm_bc_solutions: "ADMIN / SOLUTIONS", adm_bc_soumissions: "ADMIN / SUBMISSIONS",
    adm_bc_reviews: "ADMIN / REVIEWS", adm_bc_reports: "ADMIN / REPORTS", adm_bc_users: "ADMIN / USERS",
    adm_bc_pricing: "ADMIN / PLANS", adm_bc_modules: "ADMIN / MODULES",
    adm_nouvelle_solution: "New solution",
    adm_stat_solutions: "Solutions", adm_stat_actives: "Active", adm_stat_vues: "Total views",
    adm_stat_utilisateurs: "Users", adm_stat_validations: "Pending validations", adm_stat_avis: "Reviews to moderate",
    adm_titre_solutions: "Manage solutions", adm_col_score: "Score", adm_col_live: "Live",
    adm_active: "Active", adm_inactive: "Inactive",
    adm_titre_validations: "Validation workflow", adm_aucune_demande: "No request.",
    adm_col_par: "By", adm_approuver: "Approve", adm_correction: "Correction", adm_rejeter: "Reject",
    adm_titre_soumissions: "Submissions (simple form)", adm_aucune_soumission: "No submission.",
    adm_col_action: "Action", adm_valider: "Validate",
    adm_col_demandeur: "Requester",
    adm_titre_reviews: "Review moderation", adm_aucun_avis: "No review.",
    adm_col_auteur: "Author", adm_col_note: "Rating", adm_publier: "Publish",
    adm_aucun_signalement: "No report.", adm_col_message: "Message", adm_traiter: "Handle",
    adm_titre_users: "Users & subscriptions", adm_col_email: "Email", adm_col_role: "Role",
    adm_titre_pricing: "Pricing & plans", adm_enregistrer_tarifs: "Save pricing",
    adm_pricing_note: "Edit the prices (monthly subscription & one-time purchase) and the limits of each plan.",
    adm_prix_mois: "Price / month", adm_prix_unique: "One-time price", adm_nb_fiches: "Number of profiles (-1 = unlimited)",
    adm_tagline: "Tagline",
    adm_titre_modules: "Atlas Modules",
    adm_mod_editorial: "AfriFuté analyses", adm_mod_submission: "Company submission", adm_mod_claims: "Profile claiming",
    adm_mod_reviews: "Moderated reviews", adm_mod_reports: "Reports", adm_mod_seo: "Schema.org & sitemap",
    adm_mod_media: "afrifute.com articles", adm_mod_subs: "Subscriptions & limits", adm_mod_analytics: "Statistics",
    adm_mod_export: "Premium export", adm_desactive: "Disabled",
    adm_toast_publie: "Published", adm_toast_decision: "Decision saved", adm_toast_plan_maj: "Plan updated",
    adm_toast_tarifs: "Pricing saved", adm_prompt_correction: "Note / requested correction:",
    adm_tab_reverif: "Needs review", adm_stat_reverif: "Profiles to re-verify",
    adm_bc_reverif: "ADMIN / PERIODIC VERIFICATION", adm_titre_reverif: "Profiles to re-verify",
    adm_reverif_note_1: "An active profile is listed here if it has never been verified, or if its last verification is older than",
    adm_reverif_note_2: "months.",
    adm_reverif_vide: "No profile to re-verify at the moment.",
    adm_col_derniere_verif: "Last verification", adm_jamais_verifie: "Never verified",
    adm_marquer_reverifie: "Mark as verified today", adm_toast_reverifie: "Profile marked as verified",
    adm_tab_reglages: "Advanced settings", adm_bc_reglages: "ADMIN / ADVANCED SETTINGS", adm_titre_reglages: "Advanced settings",
    adm_reglages_taxonomies: "Taxonomies", adm_reglages_taxo_note: "Sectors, functions, countries, reach, audiences, types, maturity, platforms",
    adm_retirer: "Remove", adm_ajouter_terme: "New term…", adm_ajouter: "Add", adm_enregistrer_taxo: "Save taxonomies",
    adm_reglages_seo: "SEO", adm_seo_titre: "Site title", adm_seo_description: "Site description", adm_enregistrer_seo: "Save SEO",
    adm_reglages_email: "Email templates", adm_email_note: "Applies to all automatic emails sent (validations, rejections, claims…).",
    adm_email_prefixe: "Subject prefix", adm_email_signature: "Email footer signature", adm_enregistrer_email: "Save",
    adm_reglages_api: "API key", adm_api_note: "This key grants read-only public access to published profiles (name, description, sector, official website), useful for an external integration such as afrifute.com. It must be provided in the X-API-Key header of each request.",
    adm_api_cle: "Current key", adm_regenerer_cle: "Regenerate key",
    adm_confirm_regen_key: "Regenerate the API key? The old one will stop working immediately.",
    adm_reglages_journal: "Action log", adm_journal_vide: "No action recorded yet.",
    adm_col_date: "Date", adm_col_action_log: "Action", adm_col_details: "Details",
    adm_toast_taxo: "Taxonomies saved", adm_toast_seo: "SEO settings saved",
    adm_toast_email: "Email templates saved", adm_toast_cle_regen: "New key generated",
    adm_taxo_alerte_intro: "The following terms are still used by existing profiles:",
    adm_taxo_alerte_confirmer: "Remove them anyway? Affected profiles will keep their current value, but this term will no longer appear in filters.",

    // Solution profile
    sp_retour: "Back", sp_vues: "views", sp_score_atlas: "Atlas Score",
    sp_score_title: "Atlas Knowledge Score: richness, media, verification, contacts",
    sp_revendiquee: "Claimed profile", sp_badge_certifiee: "Certified solution", sp_badge_enrichi_profil: "Enriched profile",
    sp_verifie_le: "Verified on",
    sp_site_officiel: "Official website", sp_favori: "Favorite", sp_ajouter_favoris: "Add to favorites",
    sp_revendiquer: "Claim", sp_revendiquer_titre: "Claim",
    sp_claim_message: "Message to the AfriFuté team", sp_claim_preuve: "Proof (work email, website, official link…)",
    sp_envoyer_demande: "Send request",
    sp_presentation: "Overview", sp_probleme_resolu: "Problem solved", sp_public_cible: "Target audience",
    sp_classification: "Classification", sp_captures: "Screenshots", sp_capture: "screenshot",
    sp_metriques: "Key metrics", sp_informations: "Information", sp_creee_en: "Founded in", sp_langues: "Languages",
    sp_cas_usage: "Use cases", sp_fonctionnalites: "Key features", sp_services: "Services offered",
    sp_avlim_titre: "Pros and cons", sp_avantages: "Pros", sp_limites: "Cons",
    sp_tarifs: "Pricing", sp_analyse: "Our AfriFuté analysis", sp_faq: "Frequently asked questions",
    sp_similaires: "Similar solutions", sp_articles_lies: "Related AfriFuté articles",
    sp_badges_atlas: "Atlas badges", sp_video: "Video", sp_voir_video: "Watch the presentation video",
    sp_galerie: "Gallery", sp_fondateurs: "Founders", sp_equipe: "Team",
    sp_ecosysteme: "Ecosystem & recognition", sp_integrations: "Integrations", sp_partenaires: "Partners",
    sp_certifications: "Certifications", sp_distinctions: "Awards", sp_ressources: "Resource center",
    sp_avis: "Reviews", sp_aucun_avis: "No review published yet.", sp_utilisateur: "User",
    sp_votre_note: "Your rating", sp_partagez_experience: "Share your experience…", sp_soumettre_avis: "Submit my review",
    sp_export_signalement: "Export & report", sp_exporter_json: "Export JSON", sp_exporter_csv: "Export CSV",
    sp_imprimer: "Print", sp_signaler: "Report an issue",
    sp_export_reserve: "Export (PDF/CSV/JSON) and printing are reserved for Premium and Enterprise plans.",
    sp_voir_offres: "View plans", sp_type_probleme: "Issue type", sp_decrivez_probleme: "Describe the issue…",
    sp_envoyer_signalement: "Send report", sp_partager: "Share this profile", sp_copier_lien: "Copy link",
    sp_reseaux: "Social networks", sp_not_found: "Solution not found.",
    sp_toast_login_review: "Sign in to leave a review", sp_toast_review_sent: "Review submitted - pending validation",
    sp_toast_login_report: "Sign in to report an issue", sp_toast_report_sent: "Report sent to AfriFuté",
    sp_toast_export_locked: "Export reserved for Premium plans", sp_toast_claim_sent: "Claim request sent - AfriFuté validation in progress",
    sp_toast_erreur: "Error: ", sp_toast_erreur_deux: "Error: ", sp_toast_no_site: "No website provided",
    sp_toast_login_entreprise: "Sign in with a company account to claim", sp_toast_login_claim: "Sign in to claim this profile",
    sp_toast_print_locked: "Printing reserved for Premium", sp_toast_lien_copie: "Link copied!",
    sp_report_wrong: "wrong information", sp_report_fraud: "fraud", sp_report_broken: "broken link",
    sp_report_closed: "company closed", sp_report_impersonation: "impersonation", sp_report_other: "other",
  },
};
const LangCtx = createContext({ lang: "fr", t: (k) => k, toggle: () => {} });
export const useLang = () => useContext(LangCtx);
export function LangProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("atlas_lang") || "fr");
  const toggle = () => setLang((l) => { const n = l === "fr" ? "en" : "fr"; localStorage.setItem("atlas_lang", n); return n; });
  const t = (k) => (TR[lang] && TR[lang][k]) || TR.fr[k] || k;
  return <LangCtx.Provider value={{ lang, t, toggle }}>{children}</LangCtx.Provider>;
}

const FAV_KEY = "atlas_favoris";
export function getFavoris() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; }
}
export function useFavoris() {
  const [favs, setFavs] = useState(getFavoris());
  const toggle = (id) => {
    setFavs((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  };
  return { favs, toggle, isFav: (id) => favs.includes(id) };
}

const FAV_ART_KEY = "atlas_favoris_articles";
export function useFavorisArticles() {
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem(FAV_ART_KEY) || "[]"); } catch { return []; } });
  const toggle = (id) => {
    setFavs((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      localStorage.setItem(FAV_ART_KEY, JSON.stringify(next));
      return next;
    });
  };
  return { toggle, isFav: (id) => favs.includes(id) };
}
